/**
 * Query key factory.
 *
 * Keys are hierarchical so invalidation is a prefix match: invalidating
 * `qk.table(id)` drops that table's fields, views and everything under them.
 *
 * There is deliberately no key for table *rows*. Rows are not a cache of
 * independent responses — they are one windowed buffer assembled from overlapping
 * pages and then mutated in place by realtime events, optimistic edits and
 * reordering. That lives in the row window store instead.
 */
export const qk = {
  all: ['lithos'] as const,

  settings: () => [...qk.all, 'settings'] as const,

  /**
   * The administrator's view of the instance.
   *
   * Under one prefix so that leaving the admin area drops all of it: these are
   * counts of everybody's accounts and workspaces, and there is no reason to
   * hold a list of every user on the instance in a tab that is back to editing
   * a table.
   */
  admin: () => [...qk.all, 'admin'] as const,
  adminHealth: () => [...qk.admin(), 'health'] as const,
  adminSystem: () => [...qk.admin(), 'system'] as const,
  instanceId: () => [...qk.admin(), 'instanceId'] as const,
  /**
   * One page of accounts or workspaces.
   *
   * The search and the sort are in the key rather than applied afterwards: the
   * endpoint pages, so a search is a different sequence of responses and not a
   * filtered version of the same one.
   */
  adminUsers: (query: string) => [...qk.admin(), 'users', query] as const,

  workspaces: () => [...qk.all, 'workspaces'] as const,

  applications: (workspaceId: number) =>
    [...qk.all, 'applications', workspaceId] as const,
  /** The applications the signed-in member has starred in one workspace. */
  favoriteApplications: (workspaceId: number) =>
    [...qk.all, 'applications', workspaceId, 'favorites'] as const,

  /**
   * Who may open one application -- readable only by its creator and the
   * workspace admins, so it is its own key rather than part of the list.
   */
  applicationAccess: (applicationId: number) =>
    [...qk.all, 'application', applicationId, 'access'] as const,

  /**
   * One workspace's notification list.
   *
   * The realtime layer only ever invalidates this, never patches it -- see the
   * note in `features/realtime/handlers/core.ts`. Its shape is M5's to decide.
   */
  notifications: (workspaceId: number) =>
    [...qk.all, 'notifications', workspaceId] as const,

  /** The signed-in user's running and recently finished jobs. */
  jobs: () => [...qk.all, 'jobs'] as const,

  /**
   * The signed-in user's database tokens.
   *
   * One key for the whole list rather than one per workspace: the endpoint
   * returns every token the account owns in a single response and takes no
   * scope to narrow it.
   */
  databaseTokens: () => [...qk.all, 'databaseTokens'] as const,

  /** The workspaces and applications the trash can be browsed by. */
  trashStructure: () => [...qk.all, 'trash'] as const,
  /**
   * One workspace's trash, optionally narrowed to one application.
   *
   * The application id is part of the key rather than a filter applied after
   * fetching: the endpoint pages, so the two scopes are different sequences of
   * responses and cannot share a cache entry.
   */
  trashContents: (workspaceId: number, applicationId: number | null) =>
    [...qk.trashStructure(), 'contents', workspaceId, applicationId] as const,

  /**
   * An application's backups, and the schedule that takes them.
   *
   * Under the application rather than under a database: a dashboard can be
   * snapshotted too, and the endpoints take an application id.
   */
  snapshots: (applicationId: number) =>
    [...qk.all, 'application', applicationId, 'snapshots'] as const,
  snapshotSchedule: (applicationId: number) =>
    [...qk.all, 'application', applicationId, 'snapshotSchedule'] as const,
  /** Where snapshots may be kept, which is the instance's answer, not an application's. */
  snapshotDestinations: () => [...qk.all, 'snapshotDestinations'] as const,

  /** The archives a workspace has been exported into. */
  workspaceExports: (workspaceId: number) =>
    [...qk.all, 'workspace', workspaceId, 'exports'] as const,

  /**
   * What a workspace has configured for itself, in two unrelated answers.
   *
   * Under the workspace so that leaving it drops both, and separate because
   * they are separate endpoints with separate permissions -- and because the
   * agent's settings hold tokens, which is not something to keep cached
   * alongside a tree of column names.
   */
  agentSettings: (workspaceId: number) =>
    [...qk.all, 'workspace', workspaceId, 'agent'] as const,
  agentConnections: (workspaceId: number) =>
    [...qk.agentSettings(workspaceId), 'connections'] as const,
  agentModels: (workspaceId: number, service: string, alias: string) =>
    [...qk.agentSettings(workspaceId), 'models', service, alias] as const,
  rowHistorySettings: (workspaceId: number) =>
    [...qk.all, 'workspace', workspaceId, 'rowHistory'] as const,
  /** Where the MCP export tools write, which is the instance's answer. */
  mcpExportSettings: () => [...qk.all, 'mcpExportSettings'] as const,

  database: (databaseId: number) => [...qk.all, 'database', databaseId] as const,
  tables: (databaseId: number) => [...qk.database(databaseId), 'tables'] as const,
  /**
   * What has happened to a database, as the reader has narrowed it.
   *
   * A prefix and a keyed entry, the way the aggregations are: the filters are
   * part of the key because each set of them is a different paged sequence, and
   * `activityOf` is what a revert invalidates -- it has just changed rows, and
   * every narrowing of the list is about to be wrong, not only the one on
   * screen.
   */
  activityOf: (databaseId: number) => [...qk.database(databaseId), 'activity'] as const,
  activity: (databaseId: number, filters: string) =>
    [...qk.activityOf(databaseId), filters] as const,
  /** The individual row changes one operation made, which is a fixed answer. */
  operationRows: (databaseId: number, actionUuid: string) =>
    [...qk.activityOf(databaseId), 'operation', actionUuid] as const,

  table: (tableId: number) => [...qk.all, 'table', tableId] as const,
  fields: (tableId: number) => [...qk.table(tableId), 'fields'] as const,
  fieldRules: (tableId: number) => [...qk.table(tableId), 'fieldRules'] as const,
  webhooks: (tableId: number) => [...qk.table(tableId), 'webhooks'] as const,
  /**
   * How a table is kept in step with something outside this instance.
   *
   * Keyed by the sync's own id rather than under the table: the endpoints are
   * addressed that way, and the properties are a question asked of somebody
   * else's server rather than of this one.
   */
  dataSync: (dataSyncId: number) => [...qk.all, 'dataSync', dataSyncId] as const,
  dataSyncProperties: (dataSyncId: number) =>
    [...qk.dataSync(dataSyncId), 'properties'] as const,
  /** Who may open the table at all -- an admin-only question, and its own key. */
  tableAccess: (tableId: number) => [...qk.table(tableId), 'access'] as const,
  /** Which of a table's columns are hidden from whoever is asking. */
  tableRedactions: (tableId: number) => [...qk.table(tableId), 'redactions'] as const,
  /** Who one column is hidden from -- a different question, so a different key. */
  fieldRedaction: (fieldId: number) =>
    [...qk.all, 'field', fieldId, 'redaction'] as const,
  views: (tableId: number) => [...qk.table(tableId), 'views'] as const,

  /**
   * A schedule's rows, which are days rather than a window.
   *
   * Under the *table* rather than the view, unlike everything else about a
   * view, because it is the one row-shaped thing in the application that lives
   * in the Query cache: a schedule has no global row order for the row window
   * to index. Keeping it here means a row event -- which names a table and not a
   * view -- can invalidate every month of every schedule over that table with
   * one prefix.
   */
  schedulesOf: (tableId: number) => [...qk.table(tableId), 'schedule'] as const,
  scheduleRows: (tableId: number, viewId: number, window: Record<string, unknown>) =>
    [...qk.schedulesOf(tableId), viewId, window] as const,

  view: (viewId: number) => [...qk.all, 'view', viewId] as const,
  fieldOptions: (viewId: number) => [...qk.view(viewId), 'fieldOptions'] as const,
  /**
   * Every aggregation entry for a view, whatever it was computed over.
   *
   * A prefix, so that changing which aggregation a column shows can invalidate
   * the answers for every set of rows at once -- the entry for the filtered
   * view and the entry for the unfiltered one are both about to be wrong.
   */
  aggregationsOf: (viewId: number) => [...qk.view(viewId), 'aggregations'] as const,
  aggregations: (viewId: number, signature: string) =>
    [...qk.aggregationsOf(viewId), signature] as const,

  /**
   * One export job. Not under a table: an export outlives the dialog that
   * started it and is addressed by its own id, like the file it produces.
   */
  exportJob: (jobId: number) => [...qk.all, 'export', jobId] as const,

  row: (tableId: number, rowId: number) => [...qk.table(tableId), 'row', rowId] as const,
  rowHistory: (tableId: number, rowId: number) =>
    [...qk.row(tableId, rowId), 'history'] as const,
  rowComments: (tableId: number, rowId: number) =>
    [...qk.row(tableId, rowId), 'comments'] as const,

  /**
   * One dashboard application, and everything shown on it.
   *
   * A data source's *result* is nested under the dashboard rather than keyed by
   * its own id alone, so that the four dashboard events -- which all name a
   * dashboard -- can reach it, and so that leaving the page drops the numbers
   * with the widgets that asked for them.
   */
  dashboard: (dashboardId: number) => [...qk.all, 'dashboard', dashboardId] as const,
  widgets: (dashboardId: number) => [...qk.dashboard(dashboardId), 'widgets'] as const,
  dataSources: (dashboardId: number) =>
    [...qk.dashboard(dashboardId), 'dataSources'] as const,
  dataSourceResult: (dashboardId: number, dataSourceId: number) =>
    [...qk.dashboard(dashboardId), 'result', dataSourceId] as const,
  /** The catalogue of tables and views a dashboard's data sources may read. */
  integrations: (dashboardId: number) =>
    [...qk.dashboard(dashboardId), 'integrations'] as const,

  publicView: (slug: string) => [...qk.all, 'public', slug] as const,
  publicAggregations: (slug: string, signature: string) =>
    [...qk.publicView(slug), 'aggregations', signature] as const,
  form: (slug: string) => [...qk.publicView(slug), 'form'] as const,
  /**
   * The rows a form's link row questions are prefilled with, by name.
   *
   * Keyed on what was asked for rather than on the slug alone: the names come
   * out of the address bar, so two links to one form ask two different
   * questions and must not share an answer.
   */
  formPrefilledLinks: (slug: string, asked: string) =>
    [...qk.form(slug), 'prefilled-links', asked] as const,

  /**
   * The templates a workspace can install, the platform's and its own, plus its
   * archived ones. One list, filtered on the client, because it is small and a pill or a keystroke should
   * not be a request.
   */
  templates: (workspaceId: number) => [...qk.all, 'templates', workspaceId] as const,
  template: (workspaceId: number, templateId: string) =>
    [...qk.templates(workspaceId), templateId] as const,
} as const
